import _ from "lodash/fp";

const addStylesToMatchingElements = (selector) => (styles) => (content) => {
  const element = createDivElement(content);

  [...element.querySelectorAll(selector)].map((el) => {
    for (const [style, value] of Object.entries(styles)) {
      el.style.setProperty(style, value);
    }
  });

  return element.innerHTML;
};

const convertUTF8CharactersToHTMLEntitities = (content) => {
  return content
    .replace(/[\u00A0-\u9999]/gm, (c) => `&#${c.charCodeAt(0)};`)
    .replace(/&lt;/gm, "&#60;")
    .replace(/&gt;/gm, "&#62;");
};

const createDivElement = (content) => {
  const element = document.createElement("div");
  element.innerHTML = content;
  return element;
};

const removeAttributeFromMatchingElements =
  (selector) => (attribute) => (content) => {
    const element = createDivElement(content);

    [...element.querySelectorAll(selector)].map((el) =>
      el.removeAttribute(attribute),
    );

    return element.innerHTML;
  };

const removeClassFromMatchingElements =
  (selector) => (className) => (content) => {
    const element = createDivElement(content);

    [...element.querySelectorAll(selector)].map((el) => {
      el.classList.remove(className);
      el.getAttribute("class") || el.removeAttribute("class");
    });

    return element.innerHTML;
  };

const removeNamespaceFromNestedElements = (selector) => (ns) => (content) => {
  const element = createDivElement(content);
  const re = new RegExp(`^${ns}:`, "i");

  [...element.querySelectorAll(`${ns}\\:${selector}`)].map((mathNode) => {
    [...mathNode.querySelectorAll("*")].map((node) => {
      if (re.test(node.tagName)) {
        const newEl = document.createElement(node.tagName.replace(re, ""));
        newEl.append(...node.childNodes);
        node.replaceWith(newEl);
      }
    });
  });

  return element.innerHTML;
};

const removeStyleFromMatchingElements = (selector) => (styles) => (content) => {
  const element = createDivElement(content);
  let stylesObj = styles;
  if (typeof stylesObj === "string") stylesObj = { [styles]: false };

  [...element.querySelectorAll(selector)].map((el) => {
    for (const [style, value] of Object.entries(stylesObj)) {
      if (
        !value ||
        window.getComputedStyle(el).getPropertyValue(style) === value
      )
        el.style.removeProperty(style);
    }
    if (!el.style.length) el.removeAttribute("style");
  });

  return element.innerHTML;
};

const removeTrailingEmptyParagraphElements = (content) => {
  const element = createDivElement(content);

  const getLastMatchingEmptyNode = () => {
    return [...element.querySelectorAll(`*`)]
      .filter((el) =>
        /^(?:ADDRESS|ARTICLE|ASIDE|BLOCKQUOTE|DETAILS|DIALOG|DD|DIV|DL|DT|FIELDSET|FIGCAPTION|FIGURE|FOOTER|FORM|H\d|HEADER|HGROUP|HR|LI|MAIN|NAV|OL|P|PRE|SECTION|TABLE|TD|TH|TR|UL)$/.test(
          el.nodeName.toUpperCase(),
        ),
      )
      .pop();
  };

  let el = getLastMatchingEmptyNode();
  while (true) {
    if (
      !el ||
      el.tagName.toUpperCase() !== "P" ||
      /\S/.test(el.textContent) ||
      [...el.querySelectorAll("audio,img,math,video")].length
    )
      break;

    el.remove();
    el = getLastMatchingEmptyNode();
  }

  return element.innerHTML;
};

const removeUnnecessaryDivElements = (content) => {
  const element = createDivElement(content);

  // If the div is an orphaned ARIA element, remove it
  [...element.querySelectorAll(`div.qti-visually-hidden`)].map((el) => {
    const id = el.getAttribute("id");
    if (!element.querySelector(`[aria-describedby~='"${id}"']`)) {
      el.remove();
    }
  });

  // If the div is a legacy "orca:data:include" element, remove it
  [...element.querySelectorAll(`div.orca\\:data\\:include`)].map((el) => {
    el.remove();
  });

  // If the div has no class, id, or style attributes and has a single child, unwrap it
  [...element.querySelectorAll(`div:not([class],[id],[style])`)].map((el) => {
    const children = [...el.childNodes];
    if (children.length === 1) {
      el.replaceWith(...children);
    }
  });

  return element.innerHTML;
};

const removeUnnecessaryEmIElements = (content) => {
  const element = createDivElement(content);

  [...element.querySelectorAll(`em,i`)].map((el) => {
    const children = [...el.childNodes];
    // Remove elements that have no children
    if (!children.length) {
      el.remove();

      // Unwrap elements that wrap <math> elements
    } else if (children.length === 1 && /^math$/i.test(children[0].nodeName)) {
      el.replaceWith(...children);

      // Unwrap elements that wrap only text nodes with only whitespace characters
    } else if (
      children.length === 1 &&
      children[0].nodeType === 3 /* Node.TEXT_NODE */ &&
      /^\s+$/.test(children[0].nodeValue)
    ) {
      el.replaceWith(...children);
    }
  });

  return element.innerHTML;
};

const removeUnnecessaryIDAttributes =
  (accessibilityElements = []) =>
  (content) => {
    const element = createDivElement(content);

    [
      ...element.querySelectorAll(
        `[id]:not([id^=essayTask],[id^=perspective],.tab-content)`,
      ),
    ].map((el) => {
      const id = el.getAttribute("id");
      if (
        !(
          element.querySelector(`a[href='#${id}']`) ||
          element.querySelector(`[aria-describedby~='"${id}"']`) ||
          accessibilityElements.some((ae) => ae.identifier === id)
        )
      ) {
        el.removeAttribute("id");
      }
    });

    return element.innerHTML;
  };

const removeUnnecessarySpanElements = (content) => {
  const element = createDivElement(content);

  [...element.querySelectorAll(`span:not([id])`)].map((el) => {
    // If the span has an associated ARIA element or screenreader-specific content
    if (
      (el.hasAttribute("aria-describedby") &&
        element.querySelector(`#${el.getAttribute("aria-describedby")}`)) ||
      /\bqti-visually-hidden\b/.test(el.className)
    ) {
      return;
    }

    const children = [...el.childNodes];
    // Remove elements that no children
    if (!children.length) {
      el.remove();

      // Unwrap elements that have no associated class or style attributes
    } else if (!el.getAttribute("class") && !el.getAttribute("style")) {
      el.replaceWith(...children);

      // Unwrap elements with a "text-decoration: 'overline'" style that wrap <math> elements
    } else if (
      children.length === 1 &&
      /^math$/i.test(children[0].nodeName) &&
      el.style.getPropertyValue("text-decoration") === "overline"
    ) {
      el.replaceWith(...children);
    }
  });

  return element.innerHTML;
};

const removeXiTagsFromContent = (content) => {
  const element = createDivElement(content);
  const xiTagName = "xi:include";

  [...element.querySelectorAll(CSS.escape(xiTagName))].map((el) => {
    el.remove();
  });
  return element.innerHTML;
};

const unwrapMatchingElements = (selector) => (content) => {
  const element = createDivElement(content);

  [...element.querySelectorAll(selector)].map((el) => {
    const children = [...el.childNodes];
    el.replaceWith(...children);
  });

  return element.innerHTML;
};

const wrapMathMLElementsInParagraphs = (content) => {
  const element = createDivElement(content);

  [...element.querySelectorAll(`p`)].map((p) => {
    [...p.parentNode.childNodes]
      .filter((el) => /^math$/i.test(el.nodeName) && !el.closest("p"))
      .map((el) => {
        const newEl = document.createElement("p");
        el.before(newEl);
        newEl.append(el);
      });
  });

  return element.innerHTML;
};

const cleanContent =
  ({ accessibilityElements = [] }) =>
  (content) => {
    let i = 0,
      newContent = content,
      oldContent;
    do {
      oldContent = newContent;
      newContent = _.pipe([
        removeAttributeFromMatchingElements(`[xmlns=""],p[xmlns]`)(`xmlns`),
        removeAttributeFromMatchingElements(`[id^="H"]`)(`id`),
        wrapMathMLElementsInParagraphs,
        addStylesToMatchingElements(
          `th:not(.perspectiveSpacer,.perspectiveTitle)`,
        )({
          "font-weight": "normal",
          "text-align": "center",
        }),
        removeAttributeFromMatchingElements(`img`)(`alt`),
        removeClassFromMatchingElements(`.x_act_item_highlight`)(
          `x_act_item_highlight`,
        ),
        removeNamespaceFromNestedElements(`math`)(`ns5`),
        removeClassFromMatchingElements(`.math`)(`math`),
        unwrapMatchingElements(
          `div.pad8.whitebg,div.passage-scrolling,div.row,div.span6,div.stem,div.stimulus,div.stimulus_content,div[xmlns]`,
        ),
        removeStyleFromMatchingElements(`span`)({
          "background-color": "rgb(170, 170, 170)",
        }),
        removeUnnecessaryIDAttributes(accessibilityElements),
        removeUnnecessaryDivElements,
        removeUnnecessaryEmIElements,
        removeUnnecessarySpanElements,
        removeTrailingEmptyParagraphElements,
        removeXiTagsFromContent,
        convertUTF8CharactersToHTMLEntitities,
      ])(oldContent);
    } while (oldContent !== newContent && ++i < 10);
    return newContent;
  };

const updateContentData = (type) => (data) => {
  switch (type) {
    case "item":
      if (data.passthrough || !data.content) return { ...data };

      return {
        ...data,
        choiceInteractions: data.choiceInteractions
          ? data.choiceInteractions.map((interaction) => ({
              ...interaction,
              foils: interaction.foils
                ? interaction.foils.map((foil) => {
                    if (!foil.content) return { ...foil };

                    return {
                      ...foil,
                      content: cleanContent(data)(foil.content),
                    };
                  })
                : [],
            }))
          : [],
        content: cleanContent(data)(data.content),
      };
    case "passage":
      return {
        ...data,
        content: cleanContent(data)(data.content),
      };
  }
};

export {
  addStylesToMatchingElements,
  cleanContent,
  convertUTF8CharactersToHTMLEntitities,
  removeAttributeFromMatchingElements,
  removeClassFromMatchingElements,
  removeNamespaceFromNestedElements,
  removeStyleFromMatchingElements,
  removeTrailingEmptyParagraphElements,
  removeUnnecessaryDivElements,
  removeUnnecessaryEmIElements,
  removeUnnecessaryIDAttributes,
  removeUnnecessarySpanElements,
  removeXiTagsFromContent,
  unwrapMatchingElements,
  updateContentData,
  wrapMathMLElementsInParagraphs,
};
